<script lang="ts" setup>
  import { computed, toRefs } from 'vue'

  import type { EffortJsonldVueGlobalSearchDefaultReadSync } from '@/api'

  import { ExclamationTriangleIcon, ServiceCallIcon } from '@/config/icons'
  import PreviewCommon from '@/components/global-search/PreviewCommon.vue'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{ data: EffortJsonldVueGlobalSearchDefaultReadSync; omitActions?: boolean }>()
  const { data, omitActions } = toRefs(props)
  const emit = defineEmits<{
    effortSelected: [effort: EffortJsonldVueGlobalSearchDefaultReadSync]
    shortcutClicked: []
    showOverlay: [EffortJsonldVueGlobalSearchDefaultReadSync]
  }>()

  const availableHours = computed(() => data.value.availableHours ?? null) // gesamt
  const hasBudget = computed(() => availableHours.value !== null)
  const budgetSpentHours = computed(() => data.value.spentHoursWithChildren ?? 0)
  const budgetFreeHours = computed(() => data.value.freeHours)
  const budgetOverrun = computed(() => (budgetFreeHours.value === null || budgetFreeHours.value === undefined ? false : budgetFreeHours.value < 0))
  const budgetRestPercent = computed(() => (availableHours.value ? ((budgetFreeHours.value ?? 0) / availableHours.value) * 100 : 0))
  const budgetOverrunPercent = computed(() => (availableHours.value ? (budgetSpentHours.value / availableHours.value) * 100 - 100 : 0))
  const { t } = useI18n()
</script>

<template>
  <PreviewCommon
    :alt="t('Service call')"
    :data="data"
    :icon="ServiceCallIcon"
    :omit-actions="omitActions"
    @effort-selected="(e) => emit('effortSelected', e)"
    @shortcut-clicked="emit('shortcutClicked')">
    <div class="flex flex-row items-center">
      <div
        v-tooltip="{ value: data.subtitle, showDelay: 1000, escape: false, pt: { root: { class: ['w-fit max-w-[80vw]'] } }, _x: 4, modifiers: { bottom: true } }"
        class="overflow-hidden cursor-help underline decoration-dotted underline-offset-2">
        {{ $formatter.Truncate(data.subtitle, { length: 300, stripHtml: true }) }}
      </div>
      <Button
        class="min-w-fit h-fit w-fit m-1"
        label="..."
        outlined
        severity="secondary"
        size="small"
        @click.prevent.stop="emit('showOverlay', data)" />
    </div>
    <div v-if="hasBudget">
      <span
        v-if="budgetFreeHours !== null && budgetFreeHours !== undefined"
        :class="[budgetOverrun ? 'text-error' : 'text-blue-500', 'font-medium']">
        <ExclamationTriangleIcon
          v-if="budgetOverrun"
          class="inline h-4 w-4 inset-y-0" />
        Verfügbares Budget: {{ $formatter.Number(budgetFreeHours, 2) }} Std.
      </span>
      <template v-if="data.availableHours">
        <span v-if="!budgetOverrun">(Verbraucht: {{ $formatter.Number(budgetSpentHours) }} Std. von gesamt {{ $formatter.Number(data.availableHours) }} Std. ~ Restbudget: {{ $formatter.Number(budgetRestPercent) }}%)</span>
        <span v-else>(Verbraucht: {{ $formatter.Number(budgetSpentHours) }} Std. von gesamt {{ $formatter.Number(data.availableHours) }} Std. ~ Budgetüberschreitung um: {{ $formatter.Number(budgetOverrunPercent) }}%)</span>
      </template>
    </div>
  </PreviewCommon>
</template>
